<template>
    <span>
        <div class="card-body wizard-body">
            <ol class="wizard-steps list-unstyled d-flex mb-4">
                <li
                    v-for="(stepLabel, index) in stepLabels"
                    :key="stepLabel"
                    class="wizard-step flex-fill text-center"
                    :class="{ active: wizard === index + 1, done: wizard > index + 1 }"
                >
                    <span class="wizard-step-number">
                        <i v-if="wizard > index + 1" class="las la-check"></i>
                        <template v-else>{{ index + 1 }}</template>
                    </span>
                    <span class="wizard-step-label d-block">{{ stepLabel }}</span>
                </li>
            </ol>

            <template v-if="wizard == 1">
                <h5 class="mb-1">Which room are you checking?</h5>
                <p class="text-muted small mb-3">Pick the type of room, then give it a name so you can tell similar rooms apart (e.g. Master Bedroom, Bedroom 2).</p>

                <div class="form-group">
                    <div v-if="$v.form.house_area.$error" class="invalid-feedback d-block mb-2">
                        Please pick a room
                    </div>
                    <div class="button-area-inspection">
                        <div class="row mx-n1">
                            <div class="col-6 col-md-4 px-1" v-for="area in areaChoices" :key="area.id">
                                <label class="btn btn-area choice-card d-flex align-items-center p-2 w-100" :class="radioClass(form.house_area, area.id)">
                                    <input v-model="form.house_area" class="mr-2" type="radio" name="area" :value="area">
                                    <i :class="'las la-' + (area.icon || 'home') + ' la-lg mr-2'"></i>
                                    <span class="text-left flex-grow-1">{{ area.name }}</span>
                                    <i v-if="isChosen(form.house_area, area.id)" class="las la-check-circle text-primary"></i>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="isOtherArea" class="form-group">
                    <label for="wizard-area-other">Type of room *</label>
                    <input id="wizard-area-other" v-model="form.house_area_other" type="text" class="form-control" autocomplete="off" enterkeyhint="next" maxlength="255" placeholder="e.g. Gym" :class="{ 'is-invalid' : $v.form.house_area_other.$error }">
                    <div class="invalid-feedback">Please type the type of room</div>
                </div>
                <div class="form-group">
                    <label for="wizard-area-label">Room name</label>
                    <input id="wizard-area-label" v-model="form.label_house_area" type="text" class="form-control" list="area-instance-suggestions" autocomplete="off" enterkeyhint="next" placeholder="e.g. Master Bedroom" :class="{ 'is-invalid' : $v.form.label_house_area.$error }">
                    <div class="invalid-feedback">Please give this room a name</div>
                    <datalist id="area-instance-suggestions">
                        <option v-for="name in areaInstanceSuggestions" :key="name" :value="name"></option>
                    </datalist>
                </div>
            </template>

            <template v-if="wizard == 2">
                <h5 class="mb-1">What part of {{ form.label_house_area || 'the room' }} are you checking?</h5>
                <p class="text-muted small mb-3">Check one part at a time, e.g. the floor, a wall or a window. You'll tick the defects for it in the next step.</p>

                <div class="form-group">
                    <div v-if="$v.form.house_component.$error" class="invalid-feedback d-block mb-2">
                        Please pick what you are checking
                    </div>
                    <div class="button-area-inspection">
                        <div class="row mx-n1">
                            <div class="col-12 col-md-6 px-1" v-for="component in components" :key="component.id">
                                <label class="btn btn-area choice-card d-flex align-items-center p-2 w-100" :class="radioClass(form.house_component, component.id)">
                                    <input v-model="form.house_component" class="mr-2" type="radio" name="component" :value="component">
                                    <span class="text-left flex-grow-1">
                                        <small v-if="display(component).group" class="d-block text-muted text-uppercase choice-overline">{{ display(component).group }}</small>
                                        <span class="d-block">{{ display(component).title }}</span>
                                        <small v-if="display(component).detail" class="d-block text-muted choice-detail">{{ display(component).detail }}</small>
                                    </span>
                                    <i v-if="isChosen(form.house_component, component.id)" class="las la-check-circle text-primary ml-2"></i>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template v-if="wizard == 3">
                <h5 class="mb-1">Tick the defects you found</h5>
                <p class="text-muted small mb-3">
                    {{ form.label_house_area }} › {{ componentTitle }}.
                    Add a photo for each defect if you can. Nothing wrong? Just save; it records this part as checked.
                </p>
                <p v-if="form.house_component && form.house_component.context_helper" class="small bg-light rounded p-2 mb-3">
                    <i class="las la-info-circle text-primary"></i> {{ form.house_component.context_helper }}
                </p>

                <div class="form-group">
                    <label for="wizard-component-label">Name for this part</label>
                    <input id="wizard-component-label" v-model="form.label_house_component" type="text" class="form-control" list="component-instance-suggestions" autocomplete="off" placeholder="e.g. Master Bedroom Floor" :class="{ 'is-invalid' : $v.form.label_house_component.$error }">
                    <div class="invalid-feedback">Please give this part a name</div>
                    <datalist id="component-instance-suggestions">
                        <option v-for="name in componentInstanceSuggestions" :key="name" :value="name"></option>
                    </datalist>
                </div>

                <div v-if="loadingDefects" class="text-center text-muted py-4">
                    <span class="spinner-border spinner-border-sm mr-2" role="status"></span>Loading defects…
                </div>
                <assessment-defect-group-selector
                    v-else
                    :groups="defectGroups"
                    :selected-ids.sync="form.selected_defect_ids"
                    @file-change="onFileChange"
                />
            </template>

        </div>

        <div class="card-footer wizard-footer">
            <div v-if="saving" class="progress mb-2" style="height: 6px;">
                <div class="progress-bar" role="progressbar" :style="{ width: uploadPercent + '%' }"></div>
            </div>
            <div class="d-flex align-items-center">
                <button type="button" @click="cancelWizard" class="btn btn-link text-secondary px-2 mr-auto" :disabled="saving">Cancel</button>
                <button type="button" v-if="wizard > 1" @click="previousPage" class="btn btn-outline-secondary btn-form mr-2" :disabled="saving">Back</button>
                <button type="button" v-if="wizard < 3" @click="nextPage" class="btn btn-primary btn-form">Next</button>
                <button type="button" v-if="wizard == 3" class="btn btn-primary btn-form" :disabled="saving || loadingDefects" @click="handleSubmit">
                    <template v-if="saving">
                        <span class="spinner-border spinner-border-sm mr-1" role="status"></span>
                        {{ uploadPercent < 100 ? 'Uploading ' + uploadPercent + '%' : 'Saving…' }}
                    </template>
                    <template v-else>
                        Save<span v-if="form.selected_defect_ids.length"> ({{ form.selected_defect_ids.length }})</span>
                    </template>
                </button>
            </div>
        </div>

    </span>
</template>

<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import AssessmentDefectGroupSelector from './AssessmentDefectGroupSelector.vue';
import componentName from '../../utils/componentName';
import { loadDraft, saveDraft, clearDraft } from '../../utils/assessmentDraft';
import focusFirstError from '../../utils/focusFirstError';

/**
 * Picked when the room type is not in the catalogue; the owner types it in house_area_other.
 */
const OTHER_AREA = Object.freeze({ id: 'other', name: 'Other', icon: 'ellipsis-h' });

export default {
    components: { AssessmentDefectGroupSelector },
    props: {
        houseId: { type: [Object, String], required: true },
        initialArea: { type: [Object, Number], default: null },
        initialAreaLabel: { type: String, default: null },
        initialAreaOther: { type: String, default: null },
        initialAreaInstanceId: { type: Number, default: null },
        resumeDraft: { type: Boolean, default: false },
        routeStep: { type: Number, default: null },
    },
    data(){
        return {
            wizard: 1,
            maxStepReached: 1,
            stepLabels: ['Room', 'Part', 'Defects'],
            form: {
                house_area: null,
                house_area_other: null,
                house_component: null,
                selected_defect_ids: [],
                label_house_area: null,
                label_house_component: null,
            },
            areaInstanceId: this.initialAreaInstanceId,
            areas: [],
            components: [],
            defectGroups: [],
            loadingDefects: false,
            files: {},
            areaInstanceSuggestions: [],
            componentInstanceSuggestions: [],
            saving: false,
            uploadPercent: 0,
            ready: false,
        };
    },
    computed: {
        areaChoices() {
            return this.areas.concat([OTHER_AREA]);
        },
        isOtherArea() {
            return !!this.form.house_area && this.form.house_area.id === OTHER_AREA.id;
        },
        houseSlug() {
            return typeof this.houseId === 'object' ? this.houseId.slug : this.houseId;
        },
        componentTitle() {
            return this.form.house_component ? componentName(this.form.house_component.name).title : '';
        },
        hasUnsavedWork() {
            return !!(this.form.house_area || this.form.house_component || this.form.selected_defect_ids.length);
        },
    },
    beforeDestroy() {
        document.body.classList.remove('wizard-open');
    },
    async mounted() {
        document.body.classList.add('wizard-open');
        await this.loadData();

        const draft = this.resumeDraft ? loadDraft(this.houseSlug) : null;

        if (draft) {
            await this.restoreDraft(draft);
        } else if (this.initialArea || this.initialAreaOther) {
            if (this.initialArea) {
                const areaId = typeof this.initialArea === 'object' ? this.initialArea.id : this.initialArea;
                this.form.house_area = this.areas.find(a => a.id === areaId) || this.initialArea;
            } else {
                this.form.house_area = OTHER_AREA;
                this.form.house_area_other = this.initialAreaOther;
            }
            this.form.label_house_area = this.initialAreaLabel || this.defaultAreaLabel(this.form.house_area);
            this.goToStep(2, { replace: true });
        }

        this.ready = true;
    },
    validations: {
        form: {
            house_area: { required },
            house_area_other: {
                required: requiredIf(function () {
                    return this.isOtherArea;
                }),
            },
            house_component: { required },
            label_house_area: { required },
            label_house_component: { required }
        }
    },
    watch: {
        'form.house_area'(area, previousArea){
            this.areaInstanceSuggestions = [];
            if (area) {
                if (area.id !== OTHER_AREA.id) {
                    this.loadAreaInstanceSuggestions();
                }
                if (!this.form.label_house_area || (previousArea && this.form.label_house_area === this.defaultAreaLabel(previousArea))) {
                    this.form.label_house_area = this.defaultAreaLabel(area);
                }
            }
        },
        'form.house_area_other'(text, previousText){
            if (this.isOtherArea && (!this.form.label_house_area || this.form.label_house_area === previousText)) {
                this.form.label_house_area = text;
            }
        },
        'form.label_house_area'(label, previousLabel){
            // A different room name means a different area instance.
            if (this.ready && previousLabel !== null && label !== previousLabel && label !== this.initialAreaLabel) {
                this.areaInstanceId = null;
            }
        },
        'form.house_component'(component, previousComponent){
            this.componentInstanceSuggestions = [];
            if (component && this.areaInstanceId) {
                this.loadComponentInstanceSuggestions();
            }
            if (component) {
                const previousDefault = previousComponent ? this.defaultComponentLabel(previousComponent) : null;
                if (!this.form.label_house_component || this.form.label_house_component === previousDefault) {
                    this.form.label_house_component = this.defaultComponentLabel(component);
                }
            }
        },
        form: {
            deep: true,
            handler() {
                this.persistDraft();
            },
        },
        wizard() {
            this.persistDraft();
        },
        routeStep(step) {
            // Browser back/forward buttons move between steps.
            if (step && step !== this.wizard && step <= this.maxStepReached) {
                this.wizard = step;
            }
        },
    },
    methods:{
        display(component) {
            return componentName(component.name);
        },
        defaultAreaLabel(area) {
            return area.id === OTHER_AREA.id ? (this.form.house_area_other || '') : area.name;
        },
        defaultComponentLabel(component) {
            const title = componentName(component.name).title.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
            return [this.form.label_house_area, title].filter(Boolean).join(' ');
        },
        async loadAreaInstanceSuggestions(){
            try {
                const { data } = await axios.get(route("owner.assessments.areaInstances.list", [this.houseId, this.form.house_area.id]));
                this.areaInstanceSuggestions = data;
            } catch (e) {
                this.areaInstanceSuggestions = [];
            }
        },
        async loadComponentInstanceSuggestions(){
            try {
                const { data } = await axios.get(route("owner.assessments.componentInstances.list", [this.areaInstanceId, this.form.house_component.id]));
                this.componentInstanceSuggestions = data;
            } catch (e) {
                this.componentInstanceSuggestions = [];
            }
        },
        async loadData(){
            try {
                const [areas, components] = await Promise.all([
                    axios.get(route("owner.assessments.areas.list")),
                    axios.get(route("owner.assessments.components.list")),
                ]);

                this.areas = areas.data;
                this.components = components.data;
            } catch (e) {
                this.$swal('Could not load', this.messageFromError(e), 'error');
            }
        },
        async loadDefects({ keepSelection = false } = {}){
            if (!keepSelection) {
                this.form.selected_defect_ids = [];
                this.files = {};
            }
            this.loadingDefects = true;
            try {
                const { data } = await axios.get(route("owner.assessments.components.defects.list", this.form.house_component.id));
                this.defectGroups = data;
            } catch (e) {
                this.$swal('Could not load defects', this.messageFromError(e), 'error');
            } finally {
                this.loadingDefects = false;
            }
        },
        async restoreDraft(draft){
            this.form.house_area = draft.house_area_id === OTHER_AREA.id
                ? OTHER_AREA
                : (this.areas.find(a => a.id === draft.house_area_id) || null);
            this.form.house_area_other = draft.house_area_other || null;
            this.form.label_house_area = draft.label_house_area || null;
            this.areaInstanceId = draft.area_instance_id || null;
            this.form.house_component = this.components.find(c => c.id === draft.house_component_id) || null;
            this.form.label_house_component = draft.label_house_component || null;

            let step = 1;
            if (this.form.house_area && this.form.label_house_area && (!this.isOtherArea || this.form.house_area_other)) {
                step = 2;
            }
            if (step === 2 && this.form.house_component && draft.step >= 3) {
                await this.loadDefects();
                this.form.selected_defect_ids = (draft.selected_defect_ids || []).filter(id =>
                    this.defectGroups.some(group => group.defects.some(defect => defect.id === id))
                );
                step = 3;
            }

            this.goToStep(Math.min(step, draft.step || 1), { replace: true });

            const missingPhotos = (draft.photo_defect_ids || []).filter(id => this.form.selected_defect_ids.includes(id)).length;
            if (missingPhotos > 0) {
                this.$swal({
                    toast: true,
                    position: 'top',
                    icon: 'info',
                    title: `Welcome back! Please re-attach ${missingPhotos} ${missingPhotos === 1 ? 'photo' : 'photos'}.`,
                    showConfirmButton: false,
                    timer: 5000,
                });
            }
        },
        persistDraft(){
            if (!this.ready || this.saving || !this.form.house_area) {
                return;
            }
            saveDraft(this.houseSlug, {
                step: this.wizard,
                house_area_id: this.form.house_area ? this.form.house_area.id : null,
                house_area_other: this.form.house_area_other,
                label_house_area: this.form.label_house_area,
                area_instance_id: this.areaInstanceId,
                house_component_id: this.form.house_component ? this.form.house_component.id : null,
                label_house_component: this.form.label_house_component,
                selected_defect_ids: this.form.selected_defect_ids,
                photo_defect_ids: Object.keys(this.files).map(Number),
            });
        },
        radioClass(selected, value){
            return this.isChosen(selected, value) ? 'focus active' : '';
        },
        isChosen(selected, value){
            return !!selected && selected.id == value;
        },
        onFileChange({ componentDefectId, file }){
            if (file) {
                this.$set(this.files, componentDefectId, file);
            } else {
                this.$delete(this.files, componentDefectId);
            }
            this.persistDraft();
        },
        async cancelWizard(){
            if (this.hasUnsavedWork) {
                const result = await this.$swal({
                    title: 'Discard this inspection?',
                    text: 'What you have entered so far will be lost.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3F3D56',
                    confirmButtonText: 'Discard',
                    cancelButtonText: 'Keep editing',
                });
                if (!result.isConfirmed) {
                    return;
                }
            }
            clearDraft(this.houseSlug);
            this.$emit('cancel-wizard');
        },
        messageFromError(e){
            const response = e && e.response;
            if (!response) {
                return 'No internet connection. Your inspection is still here, please try again.';
            }
            if (response.status === 413) {
                return 'The photos are too large to upload. Try fewer or smaller photos.';
            }
            if (response.status === 422 && response.data && response.data.errors) {
                return Object.values(response.data.errors)[0][0];
            }
            if (response.data && response.data.message) {
                return response.data.message;
            }
            return 'Something went wrong. Please try again.';
        },
        async handleSubmit(){
            this.$v.form.$touch();
            if (this.$v.form.$anyError) {
                this.showFirstError();
                return;
            }

            const formData = new FormData();

            this.form.selected_defect_ids.forEach(id => {
                const file = this.files[id];
                if (file) {
                    formData.append('file'+id, file);
                }
            });

            const house_defects = this.form.selected_defect_ids.map(id => ({ id }));
            const payload = Object.assign({}, this.form, { house_defects });
            delete payload.selected_defect_ids;

            formData.append('form', JSON.stringify(payload));

            this.saving = true;
            this.uploadPercent = 0;

            let data;
            try {
                ({ data } = await axios.post(route("owner.assessments.store", this.houseId), formData, {
                    onUploadProgress: (event) => {
                        if (event.total) {
                            this.uploadPercent = Math.round((event.loaded / event.total) * 100);
                        }
                    },
                }));
            } catch (e) {
                this.saving = false;
                this.$swal('Not saved', this.messageFromError(e), 'error');
                return;
            }

            clearDraft(this.houseSlug);
            this.saving = false;

            const defectCount = this.form.selected_defect_ids.length;
            const result = await this.$swal({
                title: 'Saved!',
                text: defectCount
                    ? `${this.componentTitle}: ${defectCount} ${defectCount === 1 ? 'defect' : 'defects'} recorded.`
                    : `${this.componentTitle} checked, no defects.`,
                icon: 'success',
                showCancelButton: true,
                confirmButtonColor: '#3EC0AD',
                cancelButtonColor: '#3F3D56',
                confirmButtonText: `Check another part of ${this.form.label_house_area}`,
                cancelButtonText: 'Done',
                reverseButtons: true,
            });

            if (result.isConfirmed) {
                this.$emit('add-another', {
                    area: this.isOtherArea ? null : this.form.house_area,
                    areaOther: this.isOtherArea ? this.form.house_area_other : null,
                    label: this.form.label_house_area,
                    areaInstanceId: data && data.house_area_instance_id ? data.house_area_instance_id : null,
                });
            } else {
                this.$emit('complete-wizard');
            }
        },
        goToStep(step, { replace = false } = {}){
            this.wizard = step;
            this.maxStepReached = Math.max(this.maxStepReached, step);
            this.$emit('step-change', { step, replace });
            window.scrollTo(0, 0);
        },
        nextPage(){
            if(this.wizard == 1){
                this.$v.form.house_area.$touch();
                this.$v.form.house_area_other.$touch();
                this.$v.form.label_house_area.$touch();

                if (this.$v.form.house_area.$invalid || this.$v.form.house_area_other.$invalid || this.$v.form.label_house_area.$invalid) {
                    this.showFirstError();
                    return;
                }
            }

            if(this.wizard == 2){
                this.$v.form.house_component.$touch();

                if (this.$v.form.house_component.$invalid) {
                    this.showFirstError();
                    return;
                }
                this.loadDefects();
            }

            this.goToStep(this.wizard + 1);
        },
        showFirstError(){
            this.$nextTick(() => focusFirstError(this.$el, { swal: this.$swal }));
        },
        previousPage(){
            this.$emit('step-back', this.wizard - 1);
        },
    }
}
</script>

<style scoped>
.wizard-steps {
    counter-reset: step;
}
.wizard-step {
    position: relative;
    color: #94a3b8;
    font-size: 12px;
}
.wizard-step:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 15px;
    left: calc(50% + 20px);
    right: calc(-50% + 20px);
    height: 2px;
    background: #e2e8f0;
}
.wizard-step.done:not(:last-child)::after {
    background: #3EC0AD;
}
.wizard-step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid #e2e8f0;
    background: #fff;
    font-weight: 600;
    margin-bottom: 4px;
}
.wizard-step.active {
    color: #2f2b5b;
    font-weight: 600;
}
.wizard-step.active .wizard-step-number {
    border-color: #2f2b5b;
    background: #2f2b5b;
    color: #fff;
}
.wizard-step.done .wizard-step-number {
    border-color: #3EC0AD;
    background: #3EC0AD;
    color: #fff;
}
.choice-card {
    min-height: 52px;
    margin-bottom: 8px;
    white-space: normal;
}
.choice-overline {
    font-size: 10px;
    letter-spacing: 0.5px;
}
.choice-detail {
    font-size: 11px;
    line-height: 1.3;
}

/* Keep Back/Next/Save in reach of the thumb on phones */
@media (max-width: 767px) {
    .wizard-body {
        padding-bottom: 110px;
    }
    .wizard-footer {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1020;
        background: #fff;
        box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
        padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));
    }
}
</style>
